/**
 * DEV-8836 — a draft can be abandoned (or discarded as deleted) from another tab or window of
 * the same account while this tab is still filling it in. The BFF then refuses every save of
 * that draft with HTTP 409 and one of these codes in the error body.
 *
 * The code names are the BFF's wire contract (`ClaimCode` in claims-portal-edge-api).
 */
export const CLAIM_NOT_EDITABLE_CODES = ['CLAIM_ABANDONED', 'CLAIM_DELETED'] as const;

export type ClaimNotEditableCode = (typeof CLAIM_NOT_EDITABLE_CODES)[number];

const isClaimNotEditableCode = (code: string): code is ClaimNotEditableCode =>
  (CLAIM_NOT_EDITABLE_CODES as readonly string[]).includes(code);

/**
 * Why the claim can no longer be saved, read from a transformed API error — or `null` when the
 * error is anything else. Status first, code inside it: a code on any other status is ignored,
 * so an unrelated 404 or 409 keeps its usual handling.
 */
export function claimNotEditableCodeOf(error: unknown): ClaimNotEditableCode | null {
  const candidate = error as { status?: unknown; businessCodes?: unknown } | null | undefined;
  if (candidate?.status !== 409 || !Array.isArray(candidate.businessCodes)) {
    return null;
  }
  const match = candidate.businessCodes.find(
    (code): code is ClaimNotEditableCode => typeof code === 'string' && isClaimNotEditableCode(code)
  );
  return match ?? null;
}
